import { type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { Modal } from './Modal.tsx'
import { Button } from './Button.tsx'

export type ConfirmVariant = 'danger' | 'warning' | 'default'

export interface ConfirmDialogProps {
  open?: boolean
  /** Alias for open */
  isOpen?: boolean
  title: string
  message?: ReactNode
  /** Alias for message */
  description?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  variant?: ConfirmVariant
  loading?: boolean
  onConfirm: () => void
  onCancel?: () => void
  /** Alias for onCancel */
  onClose?: () => void
}

/**
 * Convenience-Wrapper fuer einfache Bestaetigungs-Dialoge.
 * Intern basiert die Komponente auf `Modal` + `Button` aus dem UI-Kit.
 */
export function ConfirmDialog({
  open,
  isOpen,
  title,
  message,
  description,
  confirmLabel = 'Loeschen',
  cancelLabel = 'Abbrechen',
  variant = 'danger',
  loading,
  onConfirm,
  onCancel,
  onClose,
}: ConfirmDialogProps) {
  const isVisible = open ?? isOpen ?? false
  const handleCancel = onCancel ?? onClose ?? (() => {})
  const body = message ?? description
  const iconTone =
    variant === 'danger'
      ? 'text-red-500'
      : variant === 'warning'
        ? 'text-amber-500'
        : 'text-brand-500'

  const confirmVariant: 'danger' | 'primary' | 'accent' =
    variant === 'danger' ? 'danger' : variant === 'warning' ? 'accent' : 'primary'

  return (
    <Modal
      open={isVisible}
      onOpenChange={(next) => {
        if (!next) handleCancel()
      }}
      size="sm"
      title={
        <span className="flex items-center gap-2">
          <AlertTriangle className={cn('h-5 w-5', iconTone)} />
          {title}
        </span>
      }
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={handleCancel} autoFocus>
            {cancelLabel}
          </Button>
          <Button variant={confirmVariant} size="sm" onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body}
    </Modal>
  )
}
